import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, ArrowRight, CheckCircle2 } from 'lucide-react';
import { AuthContext } from '../App';
import { authApi } from '../lib/api';
import { useToast } from '../hooks/use-toast';

const Signup = () => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', company: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company || !form.password) {
      toast({ title: 'Missing fields', description: 'Complete all fields to enlist.' });
      return;
    }
    setLoading(true);
    try {
      const r = await authApi.signup(form);
      login(r.data.user, r.data.token);
      toast({ title: 'Welcome aboard', description: 'Your account is ready.' });
      navigate('/app');
    } catch (err) {
      toast({ title: 'Signup failed', description: err.response?.data?.detail || 'Could not create account' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-1 relative bg-[#ffffff] border-r border-stone-200/80 overflow-hidden">
        <div className="absolute inset-0 diagonal-stripes opacity-50"></div>
        <div className="absolute inset-0 bg-grid opacity-30"></div>
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-[#4b5320] flex items-center justify-center rounded-sm">
              <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-display text-lg tracking-wider text-stone-900">
              INFANTRY <span className="text-[#4b5320]">/ DISPUTE</span>
            </div>
          </Link>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-4">What you get</div>
            <h2 className="font-display text-3xl text-stone-900 leading-tight mb-6">
              Full platform. No card. Cancel anytime.
            </h2>
            <ul className="space-y-3">
              {[
                'Unlimited client onboarding',
                'AI credit report analysis',
                'FCRA dispute letter generation',
                'Round-by-round tracking',
                'CROA-compliant safeguards'
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-stone-700">
                  <CheckCircle2 className="w-4 h-4 text-[#4b5320]" />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#f7f6f0]">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-10">
            <div className="w-9 h-9 bg-[#4b5320] flex items-center justify-center rounded-sm">
              <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-display text-lg tracking-wider">
              INFANTRY <span className="text-[#4b5320]">/ DISPUTE</span>
            </div>
          </Link>

          <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-3">Enlist</div>
          <h1 className="font-display text-3xl mb-2">Create your account.</h1>
          <p className="text-stone-700 text-sm mb-8">No credit card. Cancel anytime.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-stone-700 text-xs uppercase tracking-wider">Full Name</Label>
              <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 bg-[#fafaf6] border-stone-200 rounded-sm h-11 text-stone-900" placeholder="Jordan Carter" />
            </div>
            <div>
              <Label htmlFor="company" className="text-stone-700 text-xs uppercase tracking-wider">Company / CRO</Label>
              <Input id="company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-2 bg-[#fafaf6] border-stone-200 rounded-sm h-11 text-stone-900" placeholder="Summit Credit Solutions" />
            </div>
            <div>
              <Label htmlFor="email" className="text-stone-700 text-xs uppercase tracking-wider">Work Email</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 bg-[#fafaf6] border-stone-200 rounded-sm h-11 text-stone-900" placeholder="you@company.com" />
            </div>
            <div>
              <Label htmlFor="password" className="text-stone-700 text-xs uppercase tracking-wider">Password</Label>
              <Input id="password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2 bg-[#fafaf6] border-stone-200 rounded-sm h-11 text-stone-900" placeholder="8+ characters" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[#4b5320] hover:bg-[#3d4419] text-white font-semibold rounded-sm h-11 mt-2">
              {loading ? 'Creating...' : 'Create account'} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>

          <div className="mt-6 text-sm text-stone-500">
            Already enlisted?{' '}
            <Link to="/login" className="text-[#4b5320] hover:text-[#6b7a3f]">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
